import Cartao from '@/components/ui/Cartao';
import Chip from '@/components/ui/Chip';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import type {
  ConferenciaFinanceira,
  EventoFinanceiro,
  OndeEsta,
  RepasseFinanceiro,
} from './tipos';
import {
  corSituacao,
  dataBR,
  reais,
  rotuloSentido,
  textoSituacao,
} from './rotulos';

function LinhaValor({
  rotulo,
  valor,
  frase,
  chip,
  extra,
}: {
  rotulo: string;
  valor: string;
  frase: string;
  chip?: { tipo: 'success' | 'secondary'; texto: string };
  extra?: string[];
}) {
  return (
    <div className="border-b border-secondary-100 py-3 last:border-0">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5">
        <span className="min-w-0 text-[14px] font-medium text-foreground-800">{rotulo}</span>
        <span className="whitespace-nowrap text-[clamp(15px,4.4vw,17px)] font-bold tabular-nums text-foreground-950">
          {valor}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-foreground-500">{frase}</span>
        {chip && <Chip tipo={chip.tipo}>{chip.texto}</Chip>}
      </div>
      {extra && extra.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {extra.map((e) => (
            <li key={e} className="text-[12px] text-foreground-500">
              {e}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function BlocoOndeEsta({
  evento,
  ondeEsta,
  repasses,
  conferencia,
}: {
  evento: EventoFinanceiro;
  ondeEsta: OndeEsta;
  repasses: RepasseFinanceiro[];
  conferencia: ConferenciaFinanceira;
}) {
  const situacao = ondeEsta.situacao;

  const extraSeLiga: string[] = [];
  if (ondeEsta.repassado != null) extraSeLiga.push(`já repassado ${reais(ondeEsta.repassado)}`);
  if (Number(ondeEsta.seliga_devida ?? 0) > 0) {
    extraSeLiga.push(
      `taxa do Se Liga sobre o que ficou com vocês ${reais(ondeEsta.seliga_devida)}`,
    );
  }
  if (Number(ondeEsta.recebido_do_organizador ?? 0) > 0) {
    extraSeLiga.push(
      `vocês já pagaram ao Se Liga ${reais(ondeEsta.recebido_do_organizador)}`,
    );
  }

  return (
    <Cartao className="p-4">
      <h2 className="text-[17px] font-bold text-foreground-950">Onde está o dinheiro</h2>

      <div className="mt-1">
        <LinhaValor
          rotulo="Na sua conta do Mercado Pago"
          valor={reais(ondeEsta.na_conta_mp)}
          frase="já caiu; não passa pelo Se Liga"
          chip={
            evento.mp_conectado
              ? { tipo: 'success', texto: 'Mercado Pago conectado' }
              : { tipo: 'secondary', texto: 'sem conta conectada' }
          }
        />
        <LinhaValor
          rotulo="Recebido direto por vocês"
          valor={reais(ondeEsta.direto)}
          frase="dinheiro no caixa e pagamentos lançados à mão"
        />
        <LinhaValor
          rotulo="Com o Se Liga"
          valor={reais(ondeEsta.com_seliga)}
          frase="aguardando repasse ou acerto"
          extra={extraSeLiga}
        />
      </div>

      {textoSituacao(situacao, ondeEsta.saldo) && (
        <div
          className={`mt-3 rounded-2xl px-4 py-3 text-[14px] font-semibold ${corSituacao(situacao)}`}
        >
          {textoSituacao(situacao, ondeEsta.saldo)}
        </div>
      )}

      <div className="mt-3">
        {conferencia.destino_ok ? (
          <p className="flex items-center gap-1 text-[12px] text-success-700">
            <CheckAnimado tamanho={16} className="shrink-0" />
            Bate (sua conta + com vocês + com o Se Liga = líquido)
          </p>
        ) : (
          <p className="text-[12px] font-semibold text-warning-700">
            Os números não fecham por {reais(conferencia.diferenca_destino)}. Avise o Se Liga.
          </p>
        )}
        {conferencia.tem_teste && (
          <p className="mt-1 text-[12px] font-semibold text-warning-700">
            Há pagamento de teste neste evento.
          </p>
        )}
      </div>

      {repasses.length > 0 ? (
        <ul className="mt-3 divide-y divide-secondary-100 border-t border-secondary-100">
          {repasses.map((r, i) => (
            <li
              key={r.id ?? i}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2.5"
            >
              <span className="text-[13px] text-foreground-800">
                {dataBR(r.pago_em) || '—'} · {r.forma || '—'} · {rotuloSentido(r.sentido)}
              </span>
              <span className="text-[14px] font-bold tabular-nums text-foreground-950">
                {reais(r.valor)}
              </span>
              {r.destino && (
                <span className="w-full text-[12px] text-foreground-500">{r.destino}</span>
              )}
            </li>
          ))}
        </ul>
      ) : situacao === 'repassar' ? (
        <p className="mt-3 text-[13px] text-foreground-500">Nenhum repasse feito ainda.</p>
      ) : null}
    </Cartao>
  );
}